// The authed app shell: sidebar + topbar (docs/design/mockups/02-app.html:88-91,
// 350-371 "shared sidebar template"; brand.css:155-167 .app/.sidebar/.topbar/.main).
// Collapses to a bottom tab bar below `lg` (app.css). Renders LostAccessBanner at the
// top of every authed surface, cross-surface per slice-2-onboarding.md §11.4.
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useSession, isOwnerOrAdmin } from "../state/session.js";
import { LostAccessBanner } from "./LostAccessBanner.js";

// "Trust & Certify" (docs/design/slice4-certify-ux.md §2.2) - a small, additive entry,
// same category as the Slice-3 AppShell.title extension (slice3-knowledge-ux.md §6.2
// item 11). The ux spec's own wording is "visible to every owner/admin" (§2.2) - reuses
// the SAME `ownerAdminOnly` filter Connections already uses, not a new visibility rule
// (§2.6 flags a RICHER per-item rule as a future need, not required by this one entry).
// The destination's CONTENT gate (tier/stage) is independent and unaffected by this nav
// filter - a viewer who reaches Certify via the app-detail sub-nav tab (unfiltered,
// AppDetail.tsx) still sees the read-only surface per §2.3.
const NAV_ITEMS = [
  { to: "/apps", label: "Apps" },
  { to: "/connections", label: "Connections", ownerAdminOnly: true },
  { to: "/certify", label: "Trust & Certify", ownerAdminOnly: true },
] as const;

export const AppShell = ({
  children,
  title,
}: {
  readonly children: ReactNode;
  /** m3: the topbar showed a bare "Nightcrew" that duplicated the sidebar's own wordmark
   * (`.side-brand`, line 28 below); every caller now supplies the actual page title
   * instead (docs/design/mockups/02-app.html:93,153 - the mockup's own `.title` pattern
   * always names the page, never repeats the brand name). */
  readonly title: string;
}): React.JSX.Element => {
  const { state } = useSession();
  const canManageConnections = isOwnerOrAdmin(state);
  const items = NAV_ITEMS.filter((item) => !("ownerAdminOnly" in item) || canManageConnections);

  return (
    <div className="app-shell-root">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div className="app">
        <aside className="sidebar" aria-label="Primary">
          <div className="side-brand">
            <b style={{ fontSize: 17, letterSpacing: "-0.01em" }}>nightcrew</b>
          </div>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-i${isActive ? " active" : ""}`}
            >
              {item.label}
            </NavLink>
          ))}
          {state.status === "ready" ? (
            <div className="nav-i" style={{ marginTop: 6 }}>
              <span className="avatar">{state.user.email.charAt(0).toUpperCase()}</span>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{state.user.email}</div>
              </div>
            </div>
          ) : null}
        </aside>
        <div className="shell-main">
          <div className="topbar">
            <div className="title">{title}</div>
            {state.status === "ready" ? (
              <span className="avatar" aria-hidden="true">
                {state.user.email.charAt(0).toUpperCase()}
              </span>
            ) : null}
          </div>
          <main className="main" id="main-content">
            <LostAccessBanner />
            {children}
          </main>
        </div>
      </div>
      <nav className="mobile-tabbar" aria-label="Primary">
        {items.map((item) => (
          <NavLink key={item.to} to={item.to}>
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
